import { jsx, jsxs } from "react/jsx-runtime";
import { Box, FieldSet, AdornedInput, FormHelp, Legend, Popover, Paragraph, Heading } from "@zillow/constellation-10";
import { event } from "@zillow/universal-analytics/src/datalayer";
import React, { useRef, useEffect } from "react";
import { useFormContext, useFormState } from "react-hook-form";
import useValidFieldUpdate from "../../utils/form/useValidFieldUpdate.esm.js";
import { commaSeparateNumber, getCommaSeparatedNumber } from "../../utils/zhl-calculator.esm.js";
import { sendFieldInteractionEvent } from "./VALoanCalculator.Clickstream.esm.js";
import { FieldConfig, isSameFloat } from "./VALoanCalculator.utils.esm.js";
import { joinedSplitShell, joinedSplitMoney, joinedSplitAdornmentResetInlinePad, joinedSplitStartSymbol, joinedSplitRight, joinedSplitEndSymbol } from "./joined-split-input.styles.esm.js";
function PropertyTaxField({
  isFHAEnabled
}) {
  var _a, _b;
  const form = useFormContext();
  const proxyFormState = useFormState();
  const error = ((_a = proxyFormState == null ? void 0 : proxyFormState.errors[FieldConfig.taxRate.name]) == null ? void 0 : _a.message) || ((_b = proxyFormState == null ? void 0 : proxyFormState.errors[FieldConfig.taxRatePercent.name]) == null ? void 0 : _b.message);
  const fieldAmount = form.register(FieldConfig.taxRate.name, FieldConfig.taxRate.config);
  const fieldPercent = form.register(FieldConfig.taxRatePercent.name, FieldConfig.taxRatePercent.config);
  const amountInputRef = useRef(null);
  const formatAmount = commaSeparateNumber;
  const onOpenInteractionEvent = {
    envelope: {
      event_type_id: "5030",
      event_type_version_id: "1",
      event_template_id: "297",
      event_template_version_id: "1"
    },
    clickstream_trigger: {
      trigger_type_nm: "interaction",
      trigger_location_nm: "cms",
      trigger_source_nm: "cms_tooltip",
      trigger_object_nm: "no_trigger_object"
    },
    semantic: {
      semantic_event_nm: "cms_open"
    },
    cms_info: {
      block_type_cd: "tooltip",
      block_id: "va_mortgage_calc",
      block_displayed_txt: "Property Tax"
    }
  };
  const onCloseInteractionEvent = {
    envelope: {
      event_type_id: "2051",
      event_type_version_id: "1",
      event_template_id: "126",
      event_template_version_id: "1"
    },
    clickstream_trigger: {
      trigger_type_nm: "interaction",
      trigger_location_nm: "cms",
      trigger_source_nm: "cms_tooltip",
      trigger_object_nm: "no_trigger_object"
    },
    semantic: {
      semantic_event_nm: "cms_close"
    },
    cms_info: {
      block_type_cd: "tooltip",
      block_id: "va_mortgage_calc",
      block_displayed_txt: "Property Tax"
    }
  };
  useEffect(() => {
    if (formatAmount) {
      const defaultAmountValue = form.getValues(fieldAmount.name);
      if (defaultAmountValue && amountInputRef.current) {
        amountInputRef.current.value = formatAmount(defaultAmountValue);
      }
    }
  }, []);
  useSyncTaxRatePercent(isFHAEnabled);
  return /* @__PURE__ */ jsx(Box, { css: {
    marginBottom: "tighter"
  }, children: /* @__PURE__ */ jsx(FieldSet, { css: {
    padding: 0,
    margin: 0,
    borderWidth: 0,
    borderStyle: "solid",
    borderColor: "transparent",
    minWidth: 0,
    width: "100%",
    outline: "none"
    // Note: this field renders ~4px taller than adjacent fields (Home price, ZIP code,
    // Interest rate). Those fields use v8 constellation AdornedInput (9px vert padding);
    // this field uses v10 constellation-10 AdornedInput (12px vert padding).
    // The difference will resolve once all fields are migrated to v10.
  }, legend: /* @__PURE__ */ jsx(Legend, { children: /* @__PURE__ */ jsxs(Box, { css: {
    display: "inline-flex",
    alignItems: "center"
  }, children: [
    "Property tax",
    /* @__PURE__ */ jsx(Popover, { header: /* @__PURE__ */ jsx(Heading, { level: 6, children: "Property Tax" }), body: isFHAEnabled ? /* @__PURE__ */ jsx(Paragraph, { children: "All homeowners must pay taxes on real property, unless they qualify for an exemption. Property taxes vary by location and property value. Learn more about property taxes." }) : /* @__PURE__ */ jsx(Paragraph, { children: "The VA calculator includes estimated property taxes. The value represents an annual tax on the homeowner's property and the tax amount is based on the home's value. To convert an annual property tax in dollars to a percentage, divide the property tax by your home value and then multiply by 100." }), onOpenChange: (open) => {
      if (open) {
        event(onOpenInteractionEvent);
      } else {
        event(onCloseInteractionEvent);
      }
    } })
  ] }) }), description: /* @__PURE__ */ jsx(FormHelp, { error: Boolean(error), children: error || /* @__PURE__ */ jsx(React.Fragment, { children: " " }) }), error: Boolean(error), required: true, children: /* @__PURE__ */ jsxs(Box, { className: joinedSplitShell(Boolean(error)), children: [
    /* @__PURE__ */ jsx(AdornedInput, { className: joinedSplitMoney, error: false, startAdornment: /* @__PURE__ */ jsx(AdornedInput.Adornment, { className: joinedSplitAdornmentResetInlinePad, children: /* @__PURE__ */ jsx("span", { className: joinedSplitStartSymbol, children: "$" }) }), input: /* @__PURE__ */ jsx(AdornedInput.Input, { id: "taxRateAmount", "aria-label": "Property tax", ...fieldAmount, ref: (el) => {
      amountInputRef.current = el;
      fieldAmount.ref(el);
    }, onInput: formatAmount ? (e) => {
      const target = e.target;
      target.value = formatAmount(target.value);
    } : void 0, autoComplete: "off" }) }),
    /* @__PURE__ */ jsx(AdornedInput, { className: joinedSplitRight(Boolean(error)), error: false, endAdornment: /* @__PURE__ */ jsx(AdornedInput.Adornment, { className: joinedSplitAdornmentResetInlinePad, children: /* @__PURE__ */ jsx("span", { className: joinedSplitEndSymbol, children: "%" }) }), input: /* @__PURE__ */ jsx(AdornedInput.Input, { "aria-label": "Property tax percent", ...fieldPercent, autoComplete: "off" }) })
  ] }) }) });
}
function useSyncTaxRatePercent(isFHAEnabled) {
  const form = useFormContext();
  const lastValidFields = useRef(null);
  if (lastValidFields.current === null) {
    lastValidFields.current = form.getValues();
  }
  useValidFieldUpdate(form, (name, value) => {
    lastValidFields.current = {
      ...lastValidFields.current,
      [name]: value
    };
    if (name === FieldConfig.taxRatePercent.name) {
      const nextTaxRate = lastValidFields.current[FieldConfig.taxRatePercent.name] * lastValidFields.current[FieldConfig.homePrice.name] / 100 || 0;
      if (!isSameFloat(nextTaxRate, lastValidFields.current.taxRate)) {
        form.setValue(FieldConfig.taxRate.name, getCommaSeparatedNumber(nextTaxRate), {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
      if (!isFHAEnabled) {
        sendFieldInteractionEvent("Property tax percent", "form_field", null);
      }
    }
    if (name === FieldConfig.taxRate.name) {
      const nextTaxRate = lastValidFields.current[FieldConfig.taxRate.name] / lastValidFields.current[FieldConfig.homePrice.name] * 100 || 0;
      if (!isSameFloat(nextTaxRate, lastValidFields.current.taxRatePercent)) {
        form.setValue(FieldConfig.taxRatePercent.name, nextTaxRate, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
      if (!isFHAEnabled) {
        sendFieldInteractionEvent("Property tax amount", "form_field", null);
      }
    }
    if (name === FieldConfig.homePrice.name) {
      const nextTaxrate = lastValidFields.current[FieldConfig.taxRatePercent.name] / 100 * lastValidFields.current[FieldConfig.homePrice.name];
      if (!isSameFloat(nextTaxrate, lastValidFields.current.taxRate)) {
        form.setValue(FieldConfig.taxRate.name, getCommaSeparatedNumber(nextTaxrate), {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
    }
  });
}
export {
  PropertyTaxField as default
};
